import React, { useCallback } from 'react';
import { CustomField, TestSuite } from 'store/src/lib/tests/entity';

import { Badge } from '@/components/ui/badge';
import { ReportTestItem } from '../../utils/entities';
import FastFillText from '@/app/features/reports/components/test-tile/fast-fill-text';
import MissingValue from './missing-value';
import TextTile from './text-with-label';
import { useTestTileContext } from './context';
import { useReportViewContext } from '../report-viewer-context';
import { cn } from '@/lib/utils';

// Remove unused format functions
// const formatNumber = ...
// const formatDate = ...

// Update type to include displayValue
type CustomFieldWithValue = CustomField & {
    value: string | number | boolean | string[] | null;
    displayValue?: string | number | boolean | null; // Add pre-calculated display value
};

// Removed CustomFieldValueProps interface and CustomFieldValue component


function BooleanBadge({ value }: { value: boolean }) {

    return (
        <Badge variant="secondary" className="text-sm bg-zinc-100 border-none py-1 font-normal">{value ? 'Yes' : 'No'}</Badge>
    );
}

// components/CustomFields.tsx
interface CustomFieldsProps {
    customFields: CustomFieldWithValue[];
}

export const CustomFields: React.FC<CustomFieldsProps> = ({
    customFields,
}) => {

    const { missingData, showMissingData, fieldSettings } = useTestTileContext();
    // Filter based on raw value for showing required/missing fields
    // Ensure boolean fields are always considered visible
    const visibleFields = customFields.filter(
        field => (field.type === 'switch' || field.type === 'checkbox') // Always include booleans
            || (field.value !== null && field.value !== undefined && field.value !== '') // Include non-empty fields
            || field.required // Include required fields
            || showMissingData // Include fields when showMissingFields is true
    );

    if (!visibleFields.length) {
        return null;
    }

    const renderField = useCallback((field: CustomFieldWithValue) => {
        // Determine emptiness based on RAW value
        const isBooleanType = field.type === 'switch' || field.type === 'checkbox';
        const isMultipleSelectType = field.type === 'select' && field.selection_type === 'multiple';
        const rawValueIsEmpty = field.value === null
            || field.value === undefined
            || field.value === ''
            || (Array.isArray(field.value) && field.value.length === 0);

        // Show MissingValue component if raw value is empty AND it should be shown
        const showAsMissing = rawValueIsEmpty && !isBooleanType && (field.required || showMissingData);

        const commonProps = {
            key: field.field_id,
            title: field.label || 'missing',
            className: "",
            newLines: false
        };

        if (showAsMissing) {
            return (
                <TextTile {...commonProps}>
                    <MissingValue field={`custom.${field.field_id}`} showMissingData={showMissingData} />
                </TextTile>
            );
        }

        // Don't render if raw value is empty, not required, and not forced shown
        // (Also handle boolean false negative case if not required)
        if (rawValueIsEmpty && !field.required && !showMissingData) {
            // Specifically allow boolean false to render if showMissingFields is false
            if (!isBooleanType || field.value !== false) {
                return null;
            }
        }

        // Handle textarea separately with FastFillText
        if (field.type === 'textarea') {
            // TODO: FastFillText might need updating if it relies on raw value differently
            // Ensure FastFillText uses HighlightText internally or adjust here.
            return <FastFillText label={field.label} field_id={`custom.${field.field_id}`} />;
        }

        // Prepare children for TextTile
        let tileChildren: React.ReactNode;
        if (isBooleanType) {
            // Boolean types: Render Badge directly, no highlighting needed
            tileChildren = field.displayValue ? (
                <BooleanBadge value={true} />
            ) : (
                <BooleanBadge value={false} />
            );
        } else if (field.displayValue !== null && field.displayValue !== undefined) {

            if (isMultipleSelectType) {
                tileChildren = field.displayValue?.toString().split(', ').join("\n");
                commonProps.newLines = true;
            } else {
                // Other types: Pass the raw displayValue for TextTile to highlight
                tileChildren = field.displayValue;
            }

        } else {
            // Fallback if value is somehow null/undefined despite earlier checks
            tileChildren = null;
        }

        // Render the TextTile with appropriate children (raw value or Badge)
        return (
            <TextTile {...commonProps}>
                {tileChildren}
            </TextTile>
        );
    }, [showMissingData]);

    return (
        <>
            {visibleFields.map(renderField)}
        </>
    );
};


// Update CustomFieldContainer to pass displayValue
const CustomFieldContainer: React.FC<{ item: ReportTestItem, testSuite: TestSuite }> = ({ item, testSuite }) => {
    const { viewId } = useReportViewContext();
    const isPrintMode = viewId === 'print';

    if (!testSuite?.custom_fields || testSuite?.custom_fields?.length === 0) {
        return null;
    }

    const custom_fields: CustomFieldWithValue[] = testSuite.custom_fields
        .filter((field) => field.active)
        .map((field) => {
            const rawValue = item.custom?.[field.field_id];
            // Get the pre-calculated display value
            const displayValue = item.customDisplayValues?.[field.field_id];

            return {
                ...field,
                value: rawValue, // Keep raw value
                displayValue: displayValue // Add display value
            };
        });

    if (!custom_fields.length) {
        return null;
    }

    return (
        <div className={cn(
            isPrintMode ? "grid grid-cols-2 gap-4" : "flex-1 grid grid-cols-2 gap-4"
        )}>
            {/* Pass the updated custom_fields array */}
            <CustomFields customFields={custom_fields} />
        </div>
    );
}

export default CustomFieldContainer;

